import {
  useApi,
  useResources,
  useResourcesWithPII,
  useVimUserMetadata,
} from '@getvim/internal-vim-os-sdk/react';
import {
  ActivationStatus,
  ReferralWithoutPII,
  PatientWithoutPII,
  UpdatableReferral,
  PatientProblemList,
} from '@getvim/internal-vim-os-sdk/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logic } from '../logic';
import { useGlobalState } from '../stores/GlobalStore';
import { GlobalStateActionType } from '../stores/globalStore.types';
import { OptumReferralAppEventsEnum } from '../analytics/types/appEvents.types';
import { createLogger } from '../utils';
import { useAnalytics } from './useAnalytics';
import { ActionStatus, AppConfig, Screens, SelectAction } from '../types';
import { getProviderAnalyticsProperties } from '../analytics/parseProviderForAnalytics';
import { getReferralMode, ReferralModeEnum } from '../logic/referral-request/referralMode';
import { applyVendorWritebackOverrides } from '../logic/referral-response/applyVendorWritebackOverrides';
import { buildReferralWriteback } from './buildReferallWriteback';
import { usePrevious } from './usePrevious';
import { convertICDCodesToString } from '../utils/icdConverter';
import { shouldSetNewEhrEvent } from '../utils/shouldSetNewEhrEvent';
import { getOptumReferralIdFromNotes } from '../utils/getOptumReferralIdFromNotes';
import { parsePriorAuthStatusFromNotes } from '../utils/parsePriorAuthStatusFromNotes';
import { useAppFeatureFlags } from '../utils/appFeatureFlagsContext';
import { internalApi } from '../api';
import { formatPaStatusNote, shouldWriteBackPaStatus } from '../logic/pa-status-writeback';

const logger = createLogger('useEhrStateHandlers');

interface DeferredActivationData {
  patient: PatientWithoutPII;
  referral: ReferralWithoutPII;
  signal: AbortSignal;
  newStatus: string;
  currentNotes: string;
}

/**
 * A custom hook to handle ehr state reactive logic
 */
const useEhrStateHandlers = ({ appConfig }: { appConfig: AppConfig }) => {
  const {
    shouldGetReferralModeByDateFF,
    shouldCallOptumPatientEligibilityApiFF,
    shouldCachePatientEligibilityFF,
    shouldAutoUpdatePriorAuthStatusFF,
    shouldUseLeadingReachProviderReferToName,
    shouldUseCuroNotesText,
    shouldUseNewReferralIdFormat,
    shouldUpdateSubmittedReferralMapOnResubmit,
  } = useAppFeatureFlags();
  const { dispatch, state } = useGlobalState();
  const { ehrEvent } = useResources();
  const {
    ehrState: { patient, referral },
  } = useResourcesWithPII();
  const previousReferral: ReferralWithoutPII | undefined = usePrevious(referral);
  const previousPatient: PatientWithoutPII | undefined = usePrevious(patient);
  const { user, organization, ehrVendor } = useVimUserMetadata();

  const [submittedReferralsMap, setSubmittedReferralsMap] = useState<Record<string, string>>({});

  const {
    setActivationStatus,
    autoPopup,
    resourceUpdate,
    launchButton,
    closeApp,
    subscribeAppOpenStatus,
    pushNotification,
  } = useApi();

  const { analyticsClient } = useAnalytics();
  const [isActive, setIsActive] = useState<boolean>(false);
  const wasActive = usePrevious(isActive);

  const eligibilityCacheRef = useRef<Map<string, Promise<boolean>>>(new Map());
  const resolvedEligibilityTokensRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    eligibilityCacheRef.current.clear();
    resolvedEligibilityTokensRef.current.clear();
  }, [patient?.token]);

  //track app enabled
  const trackAppEnabled = useCallback(
    (currentPatient: PatientWithoutPII, currentReferral: ReferralWithoutPII) => {
      analyticsClient.track(OptumReferralAppEventsEnum.APP_ENABLED, {
        ehr_insurance: currentPatient?.insurance?.ehrInsurance,
        vim_patient_id: currentPatient?.identifiers?.vimPatientId,
        patient_token: currentPatient?.token,
        linked_ehr_user_name: user?.identifiers?.ehrUsername,
        organization_id: organization?.identifiers?.id,
        organization_name: organization?.identifiers?.name,
        vimReferralId: currentReferral?.identifiers?.vimReferralId,
        ehrReferralId: currentReferral?.identifiers?.ehrReferralId,
      });
      analyticsClient.track(OptumReferralAppEventsEnum.BUTTON_CALCULATED, {
        referral: currentReferral,
        button_displayed: true,
      });
    },
    [analyticsClient, organization, user],
  );

  //on app disabled
  useEffect(() => {
    if (!isActive && wasActive) {
      dispatch({
        type: GlobalStateActionType.REFERRAL_CLOSED,
        payload: {
          optumIframeId: undefined,
          url: null,
          screen: Screens.None,
        },
      });
    }
  }, [dispatch, isActive, wasActive]);

  const setInlineButtonActive = useCallback(
    (active: boolean) => {
      launchButton.enableLaunchButtons({ referral: { REFERRAL_SELECT_PROVIDER: active } });
    },
    [launchButton],
  );
  const onActiveChanged = useCallback(
    (isActiveNow) => {
      if (isActiveNow) {
        setActivationStatus(ActivationStatus.ENABLED);
        setInlineButtonActive(true);
      } else {
        setActivationStatus(ActivationStatus.DISABLED);
        setInlineButtonActive(false);
      }
    },
    [setActivationStatus, setInlineButtonActive],
  );
  useEffect(() => {
    onActiveChanged(isActive);
  }, [onActiveChanged, isActive]);

  // ----------------------------------------
  // Auto popup logic
  // ----------------------------------------
  const checkAutoPopup = useCallback(
    async (currentReferral: ReferralWithoutPII, signal: AbortSignal) => {
      const { autoPopUpMode } = appConfig;

      const referralMode = await getReferralMode({
        referral: currentReferral,
        ehrVendor,
        shouldGetReferralModeByDateFF,
      });
      const referralFirstOpened = !previousReferral;
      const referralWasSwitched =
        previousReferral &&
        previousReferral.identifiers?.vimReferralId !== currentReferral.identifiers?.vimReferralId;
      if (
        !signal.aborted &&
        (referralWasSwitched || referralFirstOpened) &&
        ((referralMode === ReferralModeEnum.CREATE && autoPopUpMode?.create) ||
          (referralMode === ReferralModeEnum.EDIT && autoPopUpMode?.edit))
      ) {
        // autoPopup() resolves even when blocked/failed — the outcome is in the result, not a
        // rejection — so a fire-and-forget call here would silently lose that signal.
        const result = await autoPopup();
        if (result.status !== 'sent') {
          logger.warning('Auto popup did not open', {
            status: result.status,
            type: result.type,
            description: result.description,
            noPHI: true,
          });
        }
      }
    },
    [appConfig, autoPopup, previousReferral, ehrVendor, shouldGetReferralModeByDateFF],
  );

  // ----------------------------------------
  // PA status gating: deferred activation data and writeback ref
  // ----------------------------------------
  const deferredActivationRef = useRef<DeferredActivationData | null>(null);

  const performPaWritebackRef = useRef(async (_newStatus: string, _currentNotes: string) => {});
  performPaWritebackRef.current = async (newStatus: string, currentNotes: string) => {
    const updatedNotes = currentNotes + '\n' + formatPaStatusNote(newStatus);
    const builder = resourceUpdate.referralBuilder();
    builder.setNotes(updatedNotes);
    await builder.commit();
  };

  //----------------------------------------
  // Set app and button status based on referral and patient
  //----------------------------------------

  const resolvePatientEligibility = useCallback(
    async (
      currentPatient: PatientWithoutPII,
      currentReferral: ReferralWithoutPII,
    ): Promise<boolean> => {
      const { insurancesPlanList, supportAllInsurances, enableEligibilityApi } = appConfig;
      const requesterZipCode = currentReferral.referringProvider?.facility?.address?.zipCode || '';
      // clear logs with shouldCachePatientEligibilityFF
      const logContext = {
        patientId: currentPatient?.identifiers?.vimPatientId,
        ehrReferralId: currentReferral?.identifiers?.ehrReferralId,
        enableEligibilityApi,
        shouldCallOptumPatientEligibilityApiFF,
        shouldCachePatientEligibilityFF,
      };
      if (!requesterZipCode) {
        logger.warning('Referring provider zip code is not set', {
          identifiers: currentReferral.identifiers,
          referringProvider: currentReferral.referringProvider,
          ...logContext,
          noPHI: true,
        });
      }

      if (!shouldCallOptumPatientEligibilityApiFF || !enableEligibilityApi || !requesterZipCode) {
        logger.info('Using isPatientEligibleByPlan', {
          requesterZipCode,
          ...logContext,
          noPHI: true,
        });
        return logic.isPatientEligibleByPlan({
          patient: currentPatient,
          insurancesPlanList,
          supportAllInsurances,
        });
      }

      if (shouldCachePatientEligibilityFF) {
        const pending = eligibilityCacheRef.current.get(currentPatient.token);
        if (pending) {
          const isResolved = resolvedEligibilityTokensRef.current.has(currentPatient.token);
          logger.info(
            isResolved
              ? 'Using cached patient eligibility'
              : 'Reusing in-flight eligibility request',
            { ...logContext, noPHI: true },
          );
          return await pending;
        }
      }

      logger.info('Using isPatientEligible api call', {
        ...logContext,
        noPHI: true,
      });
      const eligibilityPromise = logic.isPatientEligible({
        organization: organization?.identifiers?.name || '',
        deployConfig: appConfig.optumDeployConfiguration,
        patientToken: currentPatient.token,
        requesterZipCode,
      });

      if (shouldCachePatientEligibilityFF) {
        eligibilityCacheRef.current.set(currentPatient.token, eligibilityPromise);
        eligibilityPromise
          .then(() => {
            resolvedEligibilityTokensRef.current.add(currentPatient.token);
          })
          .catch(() => {
            eligibilityCacheRef.current.delete(currentPatient.token);
            resolvedEligibilityTokensRef.current.delete(currentPatient.token);
          });
      }

      return await eligibilityPromise;
    },
    [
      appConfig,
      shouldCallOptumPatientEligibilityApiFF,
      shouldCachePatientEligibilityFF,
      organization,
    ],
  );

  const toggleEnabledStatus = useCallback(
    async (
      currentPatient: PatientWithoutPII | undefined,
      currentReferral: ReferralWithoutPII | undefined,
      signal: AbortSignal,
    ): Promise<void> => {
      if (!currentPatient || !currentReferral) {
        setIsActive(false);
        return;
      }

      // Every downstream Optum interaction (opening the referral, submitting, checking PA
      // status) needs deploy config — an org missing it can never complete any of them, so block
      // activation here rather than showing an enabled app that fails as soon as it's used.
      if (!appConfig.optumDeployConfiguration) {
        logger.error('Missing Optum deploy configuration, cannot activate', {
          organizationId: organization?.identifiers?.id,
          organizationName: organization?.identifiers?.name,
          vimReferralId: currentReferral.identifiers?.vimReferralId,
          noPHI: true,
        });
        setIsActive(false);
        return;
      }
      // Local helper — captured from the useCallback closure so it stays
      // consistent for the lifetime of this invocation (including deferred
      // push-notification callbacks).
      const activateApp = (p: PatientWithoutPII, r: ReferralWithoutPII, s: AbortSignal) => {
        if (s.aborted) return;
        setIsActive(true);
        trackAppEnabled(p, r);
        checkAutoPopup(r, s).catch((error) => {
          logger.error('Error while checking auto popup', {
            error: (error as any)?.message,
            noPHI: true,
          });
          logger.error('Error while checking auto popup', { error });
        });
      };

      try {
        const isPatientEligible = await resolvePatientEligibility(currentPatient, currentReferral);

        const isReferralReady = await logic.isReferralValid({
          referral: currentReferral,
          patient: currentPatient,
        });
        logger.info('Got patient eligibility and referral info', {
          isPatientEligible,
          vimPatientId: currentPatient.identifiers?.vimPatientId,
          ehrReferralId: currentReferral.identifiers?.ehrReferralId,
          isReferralValid: isReferralReady,
          noPHI: true,
        });

        if (signal.aborted) {
          return;
        }
        if (isPatientEligible && isReferralReady) {
          if (!shouldAutoUpdatePriorAuthStatusFF) {
            activateApp(currentPatient, currentReferral, signal);
            return;
          }

          // --- PA status writeback flow (all new logic under FF) ---
          // currentReferral is the PII-enriched object at runtime (passed from the effect),
          // but typed as ReferralWithoutPII; cast to access the notes PII field.
          const notes = (currentReferral as { basicInformation?: { notes?: string } })
            ?.basicInformation?.notes;

          const paLogContext = {
            ehrReferralId: currentReferral.identifiers?.ehrReferralId,
            vimReferralId: currentReferral.identifiers?.vimReferralId,
            noPHI: true,
          };

          const optumRefId = getOptumReferralIdFromNotes(notes);
          if (!optumRefId) {
            logger.info('No Optum referral ID in notes, skipping PA status check', paLogContext);
            activateApp(currentPatient, currentReferral, signal);
            return;
          }

          let apiStatus: string | undefined;
          try {
            logger.info('Fetching PA status', {
              ...paLogContext,
              optumRefId,
              deployConfig: appConfig.optumDeployConfiguration,
            });
            const result = await internalApi.optumNoPIIApi.getPriorAuthStatus({
              refId: optumRefId,
              deployConfig: appConfig.optumDeployConfiguration,
            });
            apiStatus = result?.status;
          } catch (error) {
            logger.error('Error fetching prior auth status', {
              ...paLogContext,
              optumRefId,
              error: (error as any)?.message,
            });
          }

          if (signal.aborted) return;

          const notesStatus = parsePriorAuthStatusFromNotes(notes);
          logger.info('PA status fetched', {
            ...paLogContext,
            optumRefId,
            apiStatus,
            notesStatus,
            deployConfig: appConfig.optumDeployConfiguration,
          });
          if (!apiStatus || !shouldWriteBackPaStatus(apiStatus, notesStatus)) {
            logger.info('PA status unchanged or unavailable, activating directly', {
              ...paLogContext,
              apiStatus,
              notesStatus,
            });
            activateApp(currentPatient, currentReferral, signal);
            return;
          }

          logger.info('PA status changed, showing confirmation notification', {
            ...paLogContext,
            apiStatus,
            notesStatus,
          });

          // Enable activation status and buttons synchronously so the SDK
          // allows the push notification. setIsActive(true) is also called
          // to keep React state in sync (the onActiveChanged effect will
          // run on re-render but is idempotent).
          setActivationStatus(ActivationStatus.ENABLED);
          launchButton.enableLaunchButtons({ referral: { REFERRAL_SELECT_PROVIDER: true } });
          setIsActive(true);
          trackAppEnabled(currentPatient, currentReferral);

          deferredActivationRef.current = {
            patient: currentPatient,
            referral: currentReferral,
            signal,
            newStatus: apiStatus,
            currentNotes: notes || '',
          };

          await pushNotification.show({
            text: `Prior auth status updated to: ${apiStatus}`,
            notificationId: `pa-status-${currentReferral.identifiers?.vimReferralId}`,
            actionButtons: {
              leftButton: {
                text: 'Cancel',
                buttonStyle: 'LINK',
                callback: () => {
                  pushNotification.hide();
                  const deferred = deferredActivationRef.current;
                  if (deferred && !deferred.signal.aborted) {
                    const deferredLogContext = {
                      ehrReferralId: deferred.referral.identifiers?.ehrReferralId,
                      vimReferralId: deferred.referral.identifiers?.vimReferralId,
                      noPHI: true,
                    };
                    checkAutoPopup(deferred.referral, deferred.signal).catch((error) => {
                      logger.error('Error while checking auto popup after PA cancel', {
                        ...deferredLogContext,
                        error: (error as any)?.message,
                      });
                    });
                  }
                  deferredActivationRef.current = null;
                },
              },
              rightButton: {
                text: 'Update',
                buttonStyle: 'PRIMARY',
                callback: () => {
                  pushNotification.hide();
                  const deferred = deferredActivationRef.current;
                  if (deferred && !deferred.signal.aborted) {
                    const deferredLogContext = {
                      ehrReferralId: deferred.referral.identifiers?.ehrReferralId,
                      vimReferralId: deferred.referral.identifiers?.vimReferralId,
                      noPHI: true,
                    };
                    performPaWritebackRef
                      .current(deferred.newStatus, deferred.currentNotes)
                      .then(() => {
                        logger.info('PA status writeback completed', deferredLogContext);
                      })
                      .catch((error) => {
                        logger.error('PA status writeback failed', {
                          ...deferredLogContext,
                          error: (error as any)?.message,
                        });
                      })
                      .finally(() => {
                        if (!deferred.signal.aborted) {
                          checkAutoPopup(deferred.referral, deferred.signal).catch((error) => {
                            logger.error('Error while checking auto popup after PA writeback', {
                              ...deferredLogContext,
                              error: (error as any)?.message,
                            });
                          });
                        }
                      });
                  }
                  deferredActivationRef.current = null;
                },
              },
            },
          });
        } else {
          setIsActive(false);
        }
      } catch (error) {
        logger.error('Error while checking if referral is valid', {
          error: (error as any)?.message,
          referralId: currentReferral.identifiers?.vimReferralId,
          ehrReferralId: currentReferral.identifiers?.ehrReferralId,
          noPHI: true,
        });
        logger.error('Error while checking if referral is valid', { error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appConfig,
      checkAutoPopup,
      trackAppEnabled,
      resolvePatientEligibility,
      shouldAutoUpdatePriorAuthStatusFF,
      pushNotification,
    ],
  );
  const [_enabledStateAbortController, setEnabledStateAbortController] = useState<
    AbortController | undefined
  >();
  useEffect(() => {
    if (referral !== previousReferral || patient !== previousPatient) {
      if (deferredActivationRef.current) {
        pushNotification.hide();
        deferredActivationRef.current = null;
      }

      const controller = new AbortController();
      setEnabledStateAbortController((current) => {
        if (current) {
          current.abort();
        }
        return controller;
      });
      toggleEnabledStatus(patient, referral, controller.signal).catch((error) => {
        logger.error('Error in toggleEnabledStatus', { error });
      });
    }
  }, [
    toggleEnabledStatus,
    referral,
    patient,
    previousReferral,
    previousPatient,
    analyticsClient,
    pushNotification,
  ]);

  // ----------------------------------------
  // After referral is saved, send it to Optum
  // ----------------------------------------
  const sendReferralSavedToOptum = useCallback(
    async (ehrEvent) => {
      const eventReferral = ehrEvent.payload?.referral || ehrEvent.payload;
      // take saved optum referral id from submittedReferralsMap
      let referralOptumId = submittedReferralsMap[eventReferral?.vimReferralId];
      // take optum referral id from notes, with a reasons-text fallback for
      // EHRs whose adapters write the formatted notes block into the Reason
      // field instead (e.g. Practice Fusion, which has no notes section).
      // Scoped to this dedupe path on purpose — the PA Status flow keeps
      // reading notes only.
      const optumReferralIdFromNotes =
        getOptumReferralIdFromNotes(eventReferral?.notes) ??
        getOptumReferralIdFromNotes(eventReferral?.reasons);
      logger.info('Before compare IDs from different sources', {
        vimReferralId: eventReferral?.vimReferralId,
        referralOptumId,
        optumReferralIdFromNotes,
        ehrReferralId: eventReferral?.referralId,
        noPHI: true,
      });
      // compare to prevent sending the same referral twice if its case with broken flow
      if (optumReferralIdFromNotes && optumReferralIdFromNotes !== referralOptumId) {
        referralOptumId = optumReferralIdFromNotes;
        logger.info('Set optum referral id from Notes', {
          referralOptumId,
          optumReferralIdFromNotes,
          noPHI: true,
        });
      }

      logger.info('sendReferralSavedToOptum ehrReferralId', {
        referralOptumId,
        ehrReferralId: eventReferral?.referralId,
        noPHI: true,
      });
      await logic.sendReferralSavedToOptum({
        referral: eventReferral,
        currentReferralOptumId: referralOptumId,
        optumDeployConfiguration: appConfig.optumDeployConfiguration,
      });
    },
    [appConfig.optumDeployConfiguration, submittedReferralsMap],
  );
  const [currentEhrEvent, setCurrentEhrEvent] = useState<{ type: string }>();
  useEffect(() => {
    if (
      currentEhrEvent &&
      currentEhrEvent?.type === 'referralSaved' &&
      appConfig.optumDeployConfiguration
    ) {
      void sendReferralSavedToOptum(currentEhrEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEhrEvent]);

  useEffect(() => {
    const eventReferral = (ehrEvent as any)?.payload?.referral || (ehrEvent as any)?.payload;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { reasons, notes, ...noPHIEvent } = (eventReferral as any) || {};
    logger.info('got referralSaved event', { noPHI: true, noPHIEvent });
    if (ehrEvent?.type === 'referralSaved' && shouldSetNewEhrEvent(ehrEvent, currentEhrEvent)) {
      logger.info('set new referralSaved event', { noPHI: true });
      setCurrentEhrEvent(ehrEvent as any);
    }
  }, [ehrEvent, currentEhrEvent]);

  //////////////////////////////////////////
  // Close app
  //////////////////////////////////////////
  const onClose = useCallback(() => {
    closeApp();
    dispatch({ type: GlobalStateActionType.SET_SCREEN, payload: { screen: Screens.None } });
  }, [closeApp, dispatch]);

  //////////////////////////////////////////
  // on open flow
  // 1. Set to loading screen
  // 2. Get problem list
  // 3. Get url from optum
  // 4. Set screen to url from option
  //////////////////////////////////////////

  const onPageLoading = useCallback(
    async (
      currentPatient: PatientWithoutPII,
      currentReferral: ReferralWithoutPII,
      signal: AbortSignal,
    ) => {
      let patientProblemList: PatientProblemList;
      const onOpenContext = {
        referralId: currentReferral.identifiers?.vimReferralId,
        ehrReferralId: currentReferral.identifiers?.ehrReferralId,
        patientId: currentPatient.identifiers?.vimPatientId,
        insurance: currentPatient.insurance?.ehrInsurance,
      };

      try {
        logger.info('Start getting patientProblemList', {
          noPHI: true,
          ...onOpenContext,
        });
        const patientProblemListRaw = await currentPatient.getProblemList();
        patientProblemList = convertICDCodesToString(patientProblemListRaw);
      } catch {
        logger.warning('Error getting patient problem list', { ...onOpenContext, noPHI: true });
      }
      if (signal.aborted) {
        return;
      }

      try {
        const data = await logic.referralRequest({
          referral: currentReferral,
          patient: currentPatient,
          deployConfig: appConfig.optumDeployConfiguration,
          problemList: patientProblemList,
        });
        logger.info('Got referral data from optum', {
          noPHI: true,
          optumIframeId: data!.referenceId,
          ...onOpenContext,
        });
        if (!data) {
          logger.error('Getting invalid optum response', {
            response: data,
            ...onOpenContext,
            noPHI: true,
          });
          throw new Error('Getting invalid optum response');
        }
        if (signal.aborted) {
          return;
        }
        dispatch({
          type: GlobalStateActionType.SET_SCREEN,
          payload: {
            url: data!.redirectUrl,
            screen: Screens.OptumSearchProvider,
            optumIframeId: data!.referenceId,
          },
        });
      } catch (error) {
        logger.error('Failed getting optum response', { error, ...onOpenContext, noPHI: true });
        if (signal.aborted) {
          return;
        }
        dispatch({
          type: GlobalStateActionType.SET_SCREEN,
          payload: {
            url: null,
            screen: Screens.OptumSearchProvider,
          },
        });
      }
    },
    [appConfig.optumDeployConfiguration, dispatch],
  );
  useEffect(() => {
    const controller = new AbortController();
    if (patient && referral && state.screen === Screens.Loading) {
      onPageLoading(patient, referral, controller.signal).catch((error) => {
        logger.error('Error while opening app', { error });
      });
    }
    return () => {
      controller.abort();
    };
  }, [onPageLoading, patient, referral, state.screen]);

  const onOpen = useCallback(
    (isManual: boolean) => {
      if (!referral || !patient) {
        return;
      }
      const onOpenContext = {
        referralId: referral.identifiers?.vimReferralId,
        ehrReferralId: referral.identifiers?.ehrReferralId,
        patientId: patient?.identifiers?.vimPatientId,
        insurance: patient?.insurance?.ehrInsurance,
      };
      logger.info('Before app open', { noPHI: true, ...onOpenContext });
      if (isManual) {
        analyticsClient.track(OptumReferralAppEventsEnum.VIM_SELECT_BUTTON_CLICKED, {
          referral,
        });
      }
      dispatch({
        type: GlobalStateActionType.SET_SCREEN,
        payload: {
          screen: Screens.Loading,
        },
      });
    },
    [analyticsClient, dispatch, patient, referral],
  );

  useEffect(() => {
    const unsubscribe = subscribeAppOpenStatus((status) => {
      if (!status) {
        return;
      }

      const { isAppOpen, appOpenTrigger } = status;
      if (isAppOpen) {
        //Due to the fact that SDK functions are not ensured to complete in order,
        //there may be a case where the app autoPopup was completed after the app was set to disabled
        if (!isActive) {
          onActiveChanged(false);
        } else {
          onOpen(appOpenTrigger !== 'auto_popup');
        }
      } else {
        onClose();
      }
    });
    return unsubscribe;
  }, [subscribeAppOpenStatus, onOpen, onClose, isActive, onActiveChanged]);

  const trackTargetProviderSelectedWithVim = useCallback(
    ({
      selectedProvider,
      status,
    }: {
      selectedProvider: UpdatableReferral['targetProvider'];
      status: ActionStatus;
    }) => {
      const previousProvider = referral?.targetProvider;

      analyticsClient.track(OptumReferralAppEventsEnum.TARGET_SELECTED_WITH_VIM, {
        ...getProviderAnalyticsProperties(previousProvider, selectedProvider),
        actionDetails: {
          status,
          selectAction: SelectAction.WRITEBACK,
        },
      });
    },
    [analyticsClient, referral?.targetProvider],
  );

  const onReferralSubmit = useCallback(async () => {
    if (!state.optumIframeId) {
      throw new Error('no optumIframeId in state');
    }
    const data = await logic.referralResponse({
      referral,
      optumReferralId: state.optumIframeId,
      deployConfig: appConfig.optumDeployConfiguration,
    });
    const writeBackContext = {
      stateOptumIframeId: state.optumIframeId,
      deployConfig: appConfig.optumDeployConfiguration,
      vimReferralId: referral?.identifiers?.vimReferralId,
      oprumReferralId: data?.referralId,
    };
    if (!data || !data.referralId) {
      return logger.error("Didn't get a referral id from Optum response", {
        ...writeBackContext,
        referral: data,
        noPHI: true,
      });
    }
    const vimReferralId = referral?.identifiers?.vimReferralId;
    logger.info(`got referral from optum after response`, {
      ...writeBackContext,
      referral: data,
      noPHI: true,
    });

    if (vimReferralId) {
      const savedReferralOptumId = submittedReferralsMap[vimReferralId];

      // Write-once by default; when the FF is on, a re-submit (e.g. a specialty change
      // that creates a new Optum order) overwrites the stored id so the map reflects the
      // latest order — otherwise a later edit loads the first/stale order. Log on every
      // actual save, including the previous id, so the first submitted referral's id is
      // never lost without a trace of what changed.
      if (!savedReferralOptumId || shouldUpdateSubmittedReferralMapOnResubmit) {
        logger.info('Saving submitted referral Optum id mapping', {
          ...writeBackContext,
          referral: data,
          previousOptumId: savedReferralOptumId,
          newReferralOptumId: data.referralId,
          isOverwrite: Boolean(savedReferralOptumId),
          shouldUpdateSubmittedReferralMapOnResubmit,
          noPHI: true,
        });
        setSubmittedReferralsMap((current) => ({
          ...current,
          [vimReferralId]: data.referralId,
        }));
      } else {
        // FF off: keep the first Optum id (no-op). Retains the "Referral already saved"
        // substring for existing log searches.
        logger.info('Referral already saved, keeping first Optum id', {
          ...writeBackContext,
          referral: data,
          savedReferralOptumId,
          newReferralOptumId: data.referralId,
          shouldUpdateSubmittedReferralMapOnResubmit,
          noPHI: true,
        });
      }
    } else {
      logger.warning('Failed adding submitted referral to map, vimReferralId is missing', {
        referral,
        optumResult: data,
        noPHI: true,
      });
    }

    const [referralToUpdate, error] = await logic.getUpdateReferralByQueryResponse(data, {
      shouldUseLeadingReachProviderReferToName,
      shouldUseCuroNotesText,
      shouldUseNewReferralIdFormat,
    });
    if (!referralToUpdate || error) {
      logger.error('Failed updating referral with new data', {
        noPHI: true,
        ...writeBackContext,
        error: error?.message,
      });
      logger.error('Failed updating referral with new data', {
        error,
        ...writeBackContext,
      });
      return dispatch({
        type: GlobalStateActionType.SET_SCREEN,
        payload: {
          screen: Screens.WriteBackError,
        },
      });
    }

    logger.info('updating referral with new data', { ...writeBackContext, noPHI: true });
    try {
      const builder = resourceUpdate.referralBuilder();
      // Apply EHR-vendor-specific transformations (e.g. Practice Fusion: merge
      // notes block into reasons and cap diagnosis to 1) before handing the
      // payload to the generic adapter. No-op for vendors without overrides.
      const referralForVendor = applyVendorWritebackOverrides(referralToUpdate!, ehrVendor);
      buildReferralWriteback(builder, referralForVendor);
      await builder.commit();
      logger.info('updating referral submited', { ...writeBackContext, noPHI: true });
      trackTargetProviderSelectedWithVim({
        selectedProvider: referralToUpdate.targetProvider,
        status: ActionStatus.SUCCESS,
      });
      analyticsClient.track(OptumReferralAppEventsEnum.REFERRAL_FINISHED, {
        vimReferralId: referral?.identifiers?.vimReferralId,
        optumReferralId: data?.referralId,
      });
      onClose();
    } catch (error) {
      logger.error('writeback failed', { error, ...writeBackContext, noPHI: true });
      trackTargetProviderSelectedWithVim({
        selectedProvider: referralToUpdate.targetProvider,
        status: ActionStatus.FAILED,
      });
      return dispatch({
        type: GlobalStateActionType.SET_SCREEN,
        payload: {
          screen: Screens.WriteBackError,
        },
      });
    }

    const submittedReferralData = referralToUpdate;
    const { basicInformation, targetProvider, conditions, procedureCodes } = submittedReferralData;
    analyticsClient.track(OptumReferralAppEventsEnum.RU_REFERRAL_SUBMITTED, {
      submittedReferralData: {
        optumReferralId: state.optumIframeId,
        targetProvider: targetProvider,
        specialty: basicInformation?.specialty,
        diagnosis: conditions?.diagnosis,
        startDate: basicInformation?.startDate,
        endDate: basicInformation?.endDate,
        priority: basicInformation?.priority,
        cpts: procedureCodes?.cpts,
        numberOfVisits: basicInformation?.numberOfVisits,
      },
    });
  }, [
    analyticsClient,
    appConfig,
    dispatch,
    ehrVendor,
    onClose,
    referral,
    resourceUpdate,
    shouldUseLeadingReachProviderReferToName,
    state.optumIframeId,
    trackTargetProviderSelectedWithVim,
    submittedReferralsMap,
    shouldUseCuroNotesText,
    shouldUseNewReferralIdFormat,
    shouldUpdateSubmittedReferralMapOnResubmit,
  ]);

  // ----------------------------------------
  // Events handler
  // ----------------------------------------
  const lastAction = state.lastAction;
  const previousAction = usePrevious(lastAction);
  useEffect(() => {
    if (lastAction !== previousAction) {
      switch (lastAction?.type) {
        case GlobalStateActionType.ON_SUBMIT: {
          onReferralSubmit().catch((error) => {
            logger.error('Error while submitting referral', { error });
          });
          break;
        }
        case GlobalStateActionType.ON_CLOSE: {
          onClose();
          break;
        }
      }
    }
  }, [lastAction, onClose, onReferralSubmit, previousAction]);
};

export default useEhrStateHandlers;
